import { apiService } from 'core/services/apiService';
import { Organization } from 'types';
import { STORAGE_KEYS } from '../../store/useUserDataStore';

export interface HierarchyNode {
	id: number;
	parentId: number;
	childId: number;
	name: string;
	type: 'organization' | 'custom' | 'workplace' | 'group';
	hierarchyTypeParent: {
		id: number;
		label: string;
	};
	hierarchyTypeChild: {
		id: number;
		label: string;
	};
	editable: boolean;
}

export interface GetHierarchyParams {
	page?: number;
	itemsPerPage?: number;
	inFocus?:
		| 'organization'
		| 'workplace'
		| 'user'
		| 'organizational_hierarchy'
		| 'security_role'
		| 'partners'
		| null;
}

export interface CompleteHierarchyResponse {
	organization: Organization | null;
	hierarchyNodes: HierarchyNode[];
	workplaces: HierarchyNode[];
	combinedHierarchy: HierarchyNode[];
}

export interface CreateHierarchyNodePayload {
	parentId: number;
	name: string;
	hierarchyTypeParent: string;
	hierarchyTypeChild: string;
	childId: number | null;
	editable: boolean;
}

export interface CreateHierarchyNodeResponse {
	id: string;
	parentId: string;
	name: string;
	success: boolean;
	message?: string;
}

export interface MoveHierarchyNodePayload {
	newParentId: number;
	childId: number | null;
}

export interface MoveHierarchyNodeResponse {
	success: boolean;
	message?: string;
}

export interface DeleteHierarchyNodeResponse {
	success: boolean;
	message?: string;
}

/**
 * Fetches a paginated list of hierarchy nodes from the API.
 * Maps raw API node types to the union type `'organization' | 'custom' | 'workplace' | 'group'`.
 * Nodes with `parentId === null` are treated as organisation-level root nodes.
 *
 * @param params - Optional pagination params (`page`, `itemsPerPage`).
 * @returns An array of {@link HierarchyNode} objects, or an empty array on failure.
 */
export const getHierarchyNodes = async (
	params: GetHierarchyParams = {}
): Promise<HierarchyNode[]> => {
	try {
		const { page = 1, itemsPerPage = 50 } = params;

		const queryParams = {
			page,
			itemsPerPage,
		};

		const response = apiService.responseHandler(
			await apiService.get('/hierarchy_nodes', { params: queryParams })
		);

		// Handle response - assuming the API returns an array directly
		let nodes: HierarchyNode[] = [];

		nodes = response?.member.map((node) => {
			let type: 'custom' | 'group' | 'workplace' | 'organization' = 'custom';

			switch (node.hierarchyTypeChild?.['@id']) {
				case '/api/hierarchy_types/3':
					type = 'group';
					break;
				case '/api/hierarchy_types/2':
					type = 'workplace';
					break;
				case '/api/hierarchy_types/1':
				default:
					type = 'group';
			}
			if (node.parentId === null) {
				type = 'organization';
			}
			// console.log("node type", node.id, node.hierarchyTypeChild?.['@id'], type);
			node.type = type;
			return node;
		});

		return nodes;
	} catch (error: any) {
		console.error('Failed to fetch hierarchy nodes:', error);
		// Return empty array if API fails - we'll still show org and workplaces
		return [];
	}
};

/**
 * Builds a complete hierarchy response by fetching all hierarchy nodes and
 * separating them into typed buckets (organization, workplaces, groups).
 *
 * The result is also persisted to `localStorage` under the hierarchy nodes key
 * for offline resilience and fast initial renders.
 *
 * @param params - Optional pagination and focus filter params.
 * @returns A {@link CompleteHierarchyResponse} containing the full hierarchy breakdown.
 */
export const getCompleteHierarchy = async (
	params: GetHierarchyParams = {}
): Promise<CompleteHierarchyResponse> => {
	try {
		// Fetch hierarchy nodes - this now includes organizations, groups, and workplaces
		const hierarchyNodes = await getHierarchyNodes(params);
		// console.log("fetched hierarchy nodes", hierarchyNodes);
		// console.log('API response from hierarchy nodes:', hierarchyNodes);
		// Store in localStorage
		localStorage.setItem(STORAGE_KEYS.HIERARCHY_NODES, JSON.stringify(hierarchyNodes));

		// Separate nodes by type for backwards compatibility
		const organizations = hierarchyNodes.filter((node) => node.type === 'organization');
		const groups = hierarchyNodes.filter((node) => node.type === 'group');
		const workplaces = hierarchyNodes.filter((node) => node.type === 'workplace');

		// Convert workplaces to HierarchyNode format for backwards compatibility
		const workplacesFormatted: HierarchyNode[] = workplaces;

		const organization =
			organizations.length > 0
				? ({
						id: organizations[0].id,
						name: organizations[0].name,
						// Add other organization properties as needed
					} as Organization)
				: null;

		// console.log('Processed hierarchy data:', { organization, hierarchyNodes, workplacesFormatted });

		return {
			organization,
			hierarchyNodes: groups, // Only return groups as hierarchyNodes for backwards compatibility
			workplaces: workplacesFormatted,
			combinedHierarchy: hierarchyNodes, // Return all nodes in combined hierarchy
		};
	} catch (error: any) {
		console.error('Failed to fetch complete hierarchy:', error);
	}
};

/**
 * Create a new hierarchy node
 */
export const createHierarchyNode = async (
	payload: CreateHierarchyNodePayload
): Promise<CreateHierarchyNodeResponse> => {
	try {
		console.log('Creating hierarchy node with payload:', payload);

		const response = apiService.responseHandler(await apiService.post('/hierarchy_nodes', payload));

		console.log('Create hierarchy node response:', response);

		// Handle different possible response formats
		let result: CreateHierarchyNodeResponse;

		if (response.data) {
			result = {
				...response.data,
				success: true,
			};
		} else {
			result = {
				...response,
				success: true,
			};
		}

		return result;
	} catch (error: any) {
		console.error('Failed to create hierarchy node:', error);

		// Return error response
		return {
			id: '',
			parentId: String(payload.parentId),
			name: payload.name,
			success: false,
			message: error.message || 'Failed to create hierarchy node',
		};
	}
};

/**
 * Move a hierarchy node to a new parent
 */
export const moveHierarchyNode = async (
	nodeId: string,
	payload: MoveHierarchyNodePayload
): Promise<MoveHierarchyNodeResponse> => {
	try {
		console.log(`Moving hierarchy node ${nodeId} with payload:`, payload);

		const response = apiService.responseHandler(
			await apiService.patch(`/hierarchy_node/${nodeId}/move`, payload)
		);

		console.log('Move hierarchy node response:', response);

		return {
			success: true,
			message: response.message || 'Node moved successfully',
		};
	} catch (error: any) {
		console.error('Failed to move hierarchy node:', error);

		// Return error response
		return {
			success: false,
			message: error.message || 'Failed to move hierarchy node',
		};
	}
};

/**
 * Delete a hierarchy node
 */
export const deleteHierarchyNode = async (id: string): Promise<DeleteHierarchyNodeResponse> => {
	try {
		console.log(`Deleting hierarchy node ${id}`);
		const response = apiService.responseHandler(await apiService.delete(`/hierarchy_nodes/${id}`));
		console.log('Delete hierarchy node response:', response);
		return {
			success: true,
			message: response.message || 'Node deleted successfully',
		};
	} catch (error: any) {
		console.error('Failed to delete hierarchy node:', error);
		return {
			success: false,
			message: error.message || 'Failed to delete hierarchy node',
		};
	}
};

// Export as service object
export const hierarchyService = {
	getHierarchyNodes,
	getCompleteHierarchy,
	createHierarchyNode,
	moveHierarchyNode,
	deleteHierarchyNode,
};

export default hierarchyService;
